/**
 * Report answers held on the device until the server has them.
 *
 * The subfloor of a 1960s brick house has no signal, and a report filled in
 * there is twenty minutes of work that currently lives in a React ref. Close the
 * tab, lock the phone long enough for the tab to be evicted, or walk into a
 * tunnel and reload, and it is gone. This is the store that stops that being
 * true, and the reason the header may say "Saved on device" at all.
 *
 * IndexedDB rather than `localStorage`: the writes happen on a keystroke timer
 * and `localStorage` is synchronous on the main thread, which is exactly the
 * wrong trade on a cheap Android phone. Raw IndexedDB rather than a wrapper,
 * because this is one object store with four operations and a dependency would
 * be larger than the file.
 *
 * Keyed by `FormResponse.id`, which is the server's identifier for this report.
 * Two devices filling the same report is not a case this resolves — last write
 * wins at the server, as it does without a device store — but a device cannot
 * confuse one report's answers with another's.
 *
 * Everything here fails soft. Private browsing refuses to open a database, and
 * Safari evicts the whole origin under storage pressure. A report that cannot be
 * held on the device must still be fillable, so a failure resolves to "nothing
 * stored" and the caller reports what it can honestly claim.
 */
import type { ReportAnswers } from '../components/report-form/types.js'

const DB_NAME = 'fieldops'
const DB_VERSION = 1
const STORE = 'reportDrafts'

export type DeviceDraft = {
  responseId: string
  /** Every answer as the technician has it, not only the unsent ones. */
  answers: ReportAnswers
  /**
   * The keys the server has not acknowledged.
   *
   * Held separately so a reload can send exactly what is outstanding rather than
   * re-sending the whole report and overwriting an answer somebody in the office
   * corrected in the meantime.
   */
  dirty: string[]
  updatedAt: number
}

let opening: Promise<IDBDatabase | null> | null = null

function open(): Promise<IDBDatabase | null> {
  if (opening) return opening

  opening = new Promise<IDBDatabase | null>((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null)

    let request: IDBOpenDBRequest

    try {
      request = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      return resolve(null)
    }

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'responseId' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(null)
    // A version change from another tab, or a private window that never
    // resolves either way. The screen must not hang waiting for storage.
    request.onblocked = () => resolve(null)
  })

  return opening
}

/** One transaction, resolving to null rather than throwing on any refusal. */
async function run<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await open()
  if (!db) return null

  return new Promise<T | null>((resolve) => {
    let request: IDBRequest<T>

    try {
      const tx = db.transaction(STORE, mode)
      request = work(tx.objectStore(STORE))
    } catch {
      return resolve(null)
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(null)
  })
}

export async function readDeviceDraft(responseId: string): Promise<DeviceDraft | null> {
  const found = await run<DeviceDraft | undefined>('readonly', (store) => store.get(responseId))
  return found ?? null
}

export async function writeDeviceDraft(draft: Omit<DeviceDraft, 'updatedAt'>): Promise<void> {
  await run('readwrite', (store) => store.put({ ...draft, updatedAt: Date.now() }))
}

/**
 * Forgets a report once the server has all of it.
 *
 * Deliberately not kept as a read cache. These are photographs of the inside of
 * customers' houses described in words, on a phone that gets left in a ute, and
 * the query cache already makes a submitted report readable while online.
 */
export async function clearDeviceDraft(responseId: string): Promise<void> {
  await run('readwrite', (store) => store.delete(responseId))
}

/**
 * Drops every held draft and closes the connection.
 *
 * Used when a different person signs in on a shared device — a technician's ute
 * tablet gets handed over — and by the test environment between cases, since a
 * draft surviving from one test into the next is exactly the recovery this is
 * built to perform.
 */
export async function resetDeviceDrafts(): Promise<void> {
  const db = await open()
  db?.close()
  opening = null

  if (typeof indexedDB === 'undefined') return

  await new Promise<void>((resolve) => {
    const request = indexedDB.deleteDatabase(DB_NAME)
    request.onsuccess = () => resolve()
    request.onerror = () => resolve()
    request.onblocked = () => resolve()
  })
}
